import { SerializeQueryArgs } from '@reduxjs/toolkit/dist/query/defaultSerializeQueryArgs';
import { createApi, fakeBaseQuery } from '@reduxjs/toolkit/query/react';

import { ESeconds } from 'modules/common/constants/const';

export enum RequestType {
  BoundAccounts = 'BoundAccounts',
  BoundEmails = 'BoundEmails',
  MyBundles = 'MyBundles',
  MySubscriptions = 'MySubscriptions',
  Notifications = 'Notifications',
  NotificationsChannel = 'NotificationsChannel',
  ProjectWhitelist = 'ProjectWhitelist',
  ReferralCodes = 'ReferralCodes',
  RewardBalance = 'RewardBalance',
  RewardTxs = 'RewardTxs',
  TFAStatus = 'TFAStatus',
  TeamAccountDetails = 'TeamAccountDetails',
  TeamAccounts = 'TeamAccounts',
  TeamCreationPermission = 'TeamCreationPermission',
  TeamInvitations = 'TeamInvitations',
  TelegramNotifications = 'TelegramNotifications',
  ToSStatus = 'ToSStatus',
}

// Endpoints that must be cached by their names and params should be listed here
// Please keep in mind that if the name of an endpoint has changed,
// this list should also be updated
const endpointsSerializedByParams = [
  'checkDealDeposit',
  'checkOrGenerateDepositInvoice',
  'checkPAYGDeposit',
  'estimateAllowanceFeeAnkr',
  'estimateAllowanceFeeUsdc',
  'estimateAllowanceFeeUsdt',
  'estimateDepositFeeAnkr',
  'estimateDepositFeeUsdc',
  'estimateDepositFeeUsdt',
  'fetchAPIKeys',
  'fetchAccountStatus',
  'fetchActiveBoundEmail',
  'fetchAllowanceAnkr',
  'fetchAllowanceUsdc',
  'fetchAllowanceUsdt',
  'fetchAllowedJWTsCount',
  'fetchAssociatedAccounts',
  'fetchBlockchainTxData',
  'fetchBlockchainTxReceipt',
  'fetchBlockchains',
  'fetchBoundEmails',
  'fetchChainTimeframeData',
  'fetchDevdaoAccountJWT',
  'fetchGoogleOAuthURL',
  'fetchIsAccountCreationAllowed',
  'fetchIsDevdaoAccount',
  'fetchIsOldAccount',
  'fetchJWTStatus',
  'fetchJWTs',
  'fetchNativeTokenPrice',
  'fetchNotifications',
  'fetchOAuthAccountJWT',
  'fetchOldAccountJWT',
  'fetchOpenAPIDocumentJSON',
  'fetchOpenAPIDocumentYAML',
  'fetchPrivateStats',
  'fetchProjectTotalRequests',
  'fetchProjectTotalRequests',
  'fetchProjectWhitelist',
  'fetchProjectWhitelistBlockchains',
  'fetchProjectWhitelistBlockchains',
  'fetchProjectsWhitelistsBlockchains',
  'fetchProjectsWhitelistsBlockchains',
  'fetchPublicStats',
  'fetchRawJWTs',
  'fetchReferralCodes',
  'fetchReferralLinksByCodes',
  'fetchReferralsCount',
  'fetchReferrer',
  'fetchRewardBalance',
  'fetchRewardTxs',
  'fetchStandardAccountJWT',
  'fetchTFAStatus',
  'fetchTeamAccountDetails',
  'fetchTeamAccountJWT',
  'fetchTeamAccounts',
  'fetchToSStatus',
  'fetchTokenPrice',
  'fetchUserETHAddresses',
  'fetchWalletBalanceAnkr',
  'fetchWalletBalanceUsdc',
  'fetchWalletBalanceUsdt',
  'getCryptoDepositInvoiceDocument',
  'getStripeDepositInvoiceDocument',
];

const unserializedFields = ['totp', 'forceFetchInternal'];

// filters 2fa code to not include it to the cache key
const filterUnserializedParams = (entiries: [string, unknown][]) =>
  entiries.filter(([key]) => !unserializedFields.includes(key));

const sortQueryArgsKeys = (queryArgs: unknown) => {
  if (queryArgs && typeof queryArgs === 'object') {
    // to make sure that a cache key doesn't depend on the query args keys order
    return Object.fromEntries(
      filterUnserializedParams(Object.entries(queryArgs).sort()),
    );
  }

  return queryArgs;
};

const serializeQueryArgs: SerializeQueryArgs<unknown> = ({
  endpointName,
  queryArgs,
}) => {
  if (endpointsSerializedByParams.includes(endpointName)) {
    const sortedArgs = sortQueryArgsKeys(queryArgs);

    return JSON.stringify({ queryArgs: sortedArgs }) + endpointName;
  }

  return endpointName;
};

export const web3Api = createApi({
  baseQuery: fakeBaseQuery(),
  endpoints: () => ({}),
  // To keep endpont data after all subscribed to the endpoint components
  // are unmounted. Needs to reduce amount of requests
  keepUnusedDataFor: ESeconds.Minute,
  // To not refetch data automatically. Needs to reduce amount of requests
  refetchOnMountOrArgChange: false,
  serializeQueryArgs,
  tagTypes: [...Object.values(RequestType)],
});
